import { CERTIFICATION_COURSES } from '@common/constants/certification.constants'
import { SelectOptionType } from '@common/enums/global.enums'
import { Role } from '@common/enums/request.enums'
import { CertificationFormValues } from '@common/types/certification.types'
import { PopInButtonProps } from '@common/types/global.types'
import CheckboxInput from '@components/arthurBased/inputs/CheckboxInput'
import DatePickerInput from '@components/inputs/DatePickerInput'
import SelectInput from '@components/inputs/SelectInput'
import PopIn from '@components/layout/PopIn'
import { Stack, styled } from '@mui/material'
import { usePersonApi } from '@services/api/person.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useCertification } from '@services/contexts/CertificationContext'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

type CertificationPopInProps = {
	buttons: PopInButtonProps[]
	isOpen: boolean
	onClose: () => void
	traineeId: string
	subtitle: string
	title: string
}

/**
 * Certification creation pop-in
 * @param buttons
 * @param isOpen
 * @param onClose
 * @param traineeId
 * @param subtitle
 * @param title
 * @constructor
 */
const CertificationPopIn = (
	{
		buttons,
		isOpen,
		onClose,
		traineeId,
		subtitle,
		title
	}: CertificationPopInProps
) => {
	const { user } = useAuth()
	const { formatMessage } = useIntl()
	const { getCertificationTrainers } = usePersonApi()
	const { certification } = useCertification()

	const {
		control,
		resetField,
		watch
	} = useFormContext<CertificationFormValues>()

	const watchCourse = watch('course')
	const watchIsTrainer = watch('obtaining')
	const watchDate = watch('date')
	const watchTrainerId = watch('trainerId')

	const { data: trainers, isPending, isSuccess } = useQuery({
		queryKey: ['getCertificationTrainers', traineeId, watchCourse, watchIsTrainer, watchDate],
		queryFn: () => getCertificationTrainers(
			traineeId,
			{
				course: watchCourse,
				obtaining: {
					isTrainer: watchIsTrainer.isTrainer
				},
				date: watchDate
			}
		)
	})

	useEffect(() => {
		if (isSuccess && trainers) {
			const trainer = trainers.find(trainer => trainer.value === watchTrainerId)
			const contextTrainer = trainers.find(trainer => trainer.value === certification.trainerId)
			const isUserTrainer = trainers.find(trainer => trainer.value === user.id)

			if (trainers.length === 0) {
				// No trainer
				resetField('trainerId', { defaultValue: '' })
			} else if (trainer) {
				// Certification trainer
				resetField('trainerId', { defaultValue: trainer.value as string })
			} else if (contextTrainer) {
				// Last used trainer option
				resetField('trainerId', { defaultValue: contextTrainer.value as string })
			} else if (isUserTrainer) {
				// Connected User
				resetField('trainerId', { defaultValue: isUserTrainer.value as string })
			} else {
				// First trainer found
				resetField('trainerId', { defaultValue: trainers[0].value as string })
			}
		}
	}, [isSuccess, trainers])

	return (
		<PopIn
			buttons={buttons}
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			subtitle={subtitle}
		>
			<Form>
				<SelectInput
					control={control}
					name="course"
					label={formatMessage({ id: 'global.fields.course' })}
					placeholder={formatMessage({ id: 'components.certificationPopIn.fields.course.placeholder' })}
					options={CERTIFICATION_COURSES.map(course => ({
						type: SelectOptionType.OPTION,
						label: formatMessage({ id: `global.enums.certificationCourse.${course}` }),
						value: course
					}))}
					isRequired
				/>
				{user.role === Role.ADMIN && (
					<CheckboxInput
						control={control}
						name="obtaining"
						options={[{
							type: SelectOptionType.OPTION,
							label: formatMessage({ id: 'components.certificationPopIn.fields.isTrainer.label' }),
							value: 'isTrainer'
						}]}
					/>
				)}
				<DatePickerInput
					control={control}
					name="date"
					label={formatMessage({ id: 'components.certificationPopIn.fields.date.label' })}
					isRequired
				/>
				<SelectInput
					control={control}
					name="trainerId"
					label={formatMessage({ id: 'global.fields.trainer' })}
					placeholder={formatMessage({ id: `components.certificationPopIn.fields.trainer.${trainers?.length ? 'placeholder' : 'noData'}` })}
					options={trainers ?? []}
					isDisabled={isPending || !trainers?.length}
					isRequired
				/>
			</Form>
		</PopIn>
	)
}

const Form = styled(Stack)({
	rowGap: '12px'
})

export default CertificationPopIn
